import { defineStore } from 'pinia'
import { useOptionsStore } from './options'
import UserService from '@/api/UserService'

export const OEMS = {
    washtec: 'washtec',
}

export const SIDEBAR_MODES = {
    DEFAULT: 'default',
    HORIZONTAL: 'horizontal',
    COLLAPSED: 'collapsed',
}

export const USER_TYPES = {
    EXTERNAL: 'external',
    INTERNAL: 'internal',
}

export const useUserStore = defineStore('user', {
    state: () => ({
        userData: {
            permissions: [],
            digitalId: {},
            home: '',
            company_name: '',
            contact_email: '',
            id: null,
            oem: '',
            email: '',
            roles: [],
            name: '',
            preferred_locale: '',
            preferred_date_format: '',
            preferred_number_format: '',
            invoice_mail_send_copy_to: '',
            currency_code: '',
            street: '',
            postal_code: '',
            city: '',
            country_id: null,
            vat_identification_number: '',
            company_registration_number: '',
            isSidebarCollapsed: false,
            isSidebarManuallyCollapsed: null,
            allowed_currency_options: [],
            user_type: '',
            owned_by: null,
            owner_email: null,
            registered_at: null,
            account_plan: null,
            onboarding_status: null,
            is_partner: false,
        },

        userSidebarMode: null,
        loading: false,
        saving: false,
        switchingOem: false,
    }),

    getters: {
        can: (state) => (input) => {
            if (state.userData.permissions.length === 0) return false
            if (Array.isArray(input)) {
                return state.userData.permissions.some((permission) => {
                    return input.includes(permission)
                })
            }
            return state.userData.permissions.includes(input)
        },

        getLocale: (state) => state.userData.preferred_locale,

        isNavigationGroupShown: (state) => (neededPermissionsList) => {
            return neededPermissionsList.some((permissionName) =>
                state.userData.permissions.includes(permissionName)
            )
        },

        getUserType: (state) => {
            return state.userData.user_type || USER_TYPES.EXTERNAL
        },

        isInternalUser: (state) => {
            return state.userData.user_type === USER_TYPES.INTERNAL
        },

        isExternalUser: (state) => {
            return state.userData.user_type === USER_TYPES.EXTERNAL
        },

        isPartner: (state) => {
            return !!state.userData?.is_partner
        },
    },

    actions: {
        resetUser() {
            this.userData = {
                permissions: [],
                digitalId: {},
                home: '',
                company_name: '',
                contact_email: '',
                id: null,
                oem: '',
                email: '',
                roles: [],
                name: '',
                preferred_locale: '',
                preferred_date_format: '',
                preferred_number_format: '',
                invoice_mail_send_copy_to: '',
                currency_code: '',
                street: '',
                postal_code: '',
                city: '',
                country_id: null,
                vat_identification_number: '',
                company_registration_number: '',
                isSidebarCollapsed: false,
                isSidebarManuallyCollapsed: null,
                allowed_currency_options: [],
                user_type: '',
                owned_by: null,
                owner_email: null,
                registered_at: null,
                account_plan: null,
                onboarding_status: null,
                is_partner: false,
            }
            this.loading = false
            this.saving = false
        },

        updateUser({ fieldName, newValue }) {
            this.userData[fieldName] = newValue
        },

        setUserData(userData) {
            const { lists, ...rest } = userData
            this.userData = {
                ...this.userData,
                ...rest,
            }

            this.i18n.global.locale = this.userData.preferred_locale

            if (!this.actAsUser) {
                this.actAsUser = this.userData.id
            }

            const optionsStore = useOptionsStore()
            const moduleNames = Object.keys(lists)
            moduleNames.forEach((moduleName) => {
                optionsStore.addList({
                    moduleName,
                    list: lists[moduleName],
                })
            })
        },

        async getUserData(id = null) {
            this.loading = true
            try {
                const response = await UserService.fetchUser()
                if (!response) return

                Object.keys(response).forEach((key) => {
                    if (this.userData[key] !== undefined) {
                        this.userData[key] = response[key]
                    }
                })
            } catch (error) {
                console.error('Error fetching user (stores/user/getUserData):', error)
            } finally {
                this.loading = false
            }
        },

        async fetchDigitalId() {
            this.loading = true
            try {
                const response = await UserService.fetchDigitalId()
                if (response !== null) {
                    this.userData.digitalId = response
                }
            } catch (error) {
                console.error('Error fetching digital ID (stores/user/fetchDigitalId):', error)
            } finally {
                this.loading = false
            }
        },

        async saveUser() {
            this.saving = true
            try {
                await UserService.saveUser(this.userData)
                window.location.reload()
            } catch (error) {
                console.error('Error saving user (stores/user/saveUser):', error)
            } finally {
                this.saving = false
            }
        },

        async switchOem({ owned_by, owner_email }) {
            this.switchingOem = true
            try {
                await UserService.switchOem({ id: owned_by })
                this.userData.owned_by = owned_by
                this.userData.owner_email = owner_email
            } catch (error) {
                console.error('Error switching OEM (stores/user/switchOem):', error)
            } finally {
                this.switchingOem = false
            }
        },
    },
})
